import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import Box, { BoxProps } from 'carbon-react/lib/components/box';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import { Option } from 'carbon-react/lib/components/select';
import SimpleSelect, { CustomSelectChangeEvent } from 'carbon-react/lib/components/select/simple-select';

import { ConsolidationSubEntityForecastListItem } from '@futrli-sdk/consolidations';

import styles from './sub-entity-forecast-select.scss';

export interface SubEntityForecastSelectProps extends BoxProps {
  error?: string;
  forecasts: ConsolidationSubEntityForecastListItem[];
  isChecked?: boolean;
  name: string;
  organisationId: string;
  onSelectForecast?: (entityId: string, forecastId: string) => void;
  onChangeCheckbox?: (entityId: string) => void;
  type: 'budget' | 'forecast';
  value?: string;
}

export const SubEntityForecastSelect = ({
  error,
  forecasts,
  isChecked,
  name,
  organisationId,
  onSelectForecast,
  onChangeCheckbox,
  type,
  value,
  ...props
}: SubEntityForecastSelectProps) => {
  const intl = useIntl();

  const handleCheckboxChange = () => {
    onChangeCheckbox?.(organisationId);
  };

  const handleSelect = (event: CustomSelectChangeEvent | React.ChangeEvent<HTMLInputElement>) => {
    onSelectForecast?.(organisationId, event.target.value);
  };

  const label = (() => {
    if (type === 'budget') {
      return intl.formatMessage({
        id: 'predict.create_forecast.select_budget',
        defaultMessage: 'Select budget'
      });
    } else {
      return intl.formatMessage({
        id: 'predict.create_forecast.select_forecast',
        defaultMessage: 'Select forecast'
      });
    }
  })();

  return (
    <Box className={clsx(styles.container, { [styles.selected]: isChecked })} {...props}>
      <Checkbox checked={Boolean(isChecked)} onChange={handleCheckboxChange} label={name} size="large" mb={0} />
      {isChecked && (
        <SimpleSelect
          error={error}
          id={`sub-entity-forecast-${organisationId}`}
          label={label}
          listMaxHeight={480}
          name={`sub-entity-forecast-${organisationId}`}
          onChange={handleSelect}
          value={value ?? ''}
          required
          mt={2}
          mb={0}
        >
          <Option
            disabled
            text={intl.formatMessage({
              id: 'common.predict.please_select',
              defaultMessage: 'Please select...'
            })}
          />
          {forecasts.map(forecast => (
            <Option key={forecast.id} value={forecast.id} text={forecast.name} />
          ))}
        </SimpleSelect>
      )}
    </Box>
  );
};
